import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { AppHeader } from './AppHeader'
import { AppToasts } from './AppToasts'
import { FAB } from './FAB'
import { Sidebar } from './Sidebar'
import { GlobalSearchModal } from './GlobalSearchModal'
// EarthquakeAlertBanner intentionally unmounted (2026-07-06, Ron) — bottom
// overlay felt intrusive; rethink the quake-alert + mark-safe surface later.
import ImpersonationBanner from '@/components/admin/ImpersonationBanner'
import { useUIStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import { useSOSStore } from '@/stores/sos'
import { usePanicStore } from '@/stores/panic'
import { useProfilesStore } from '@/stores/profiles'
import { useMessagingStore } from '@/stores/messaging'
import { useSearchModalStore } from '@/stores/searchModal'
import { usePushToken } from '@/lib/usePushToken'

interface Props {
  children: React.ReactNode
  hideHeader?: boolean
  hideFab?: boolean
}

export function AppShell({ children, hideHeader = false, hideFab = false }: Props) {
  const sidebarExpanded = useUIStore((s) => s.sidebarExpanded)
  const uid = useAuthStore((s) => s.uid())
  const openSearch = useSearchModalStore((s) => s.open)
  const { pathname } = useLocation()
  const mainRef = useRef<HTMLElement>(null)
  const [headerScrollHidden, setHeaderScrollHidden] = useState(false)
  usePushToken()

  useEffect(() => {
    if (pathname.startsWith('/messaging/')) return
    mainRef.current?.scrollTo(0, 0)
    setHeaderScrollHidden(false)
  }, [pathname])

  const scrollHideEnabled = /^\/(community|find-help|hazards|weather|messaging|sessions|location|mission-control|org\/notes|org\/enrolled|org\/camera|org\/sos|org\/location|feedback)/.test(pathname)

  useEffect(() => {
    const el = mainRef.current
    if (!el) return
    let lastY = 0
    function onScroll() {
      if (window.innerWidth >= 1024 || !scrollHideEnabled) { setHeaderScrollHidden(false); lastY = el!.scrollTop; return }
      const y = el!.scrollTop
      if (y < 60) { setHeaderScrollHidden(false); lastY = y; return }
      setHeaderScrollHidden(y > lastY)
      lastY = y
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [scrollHideEnabled])

  useEffect(() => {
    if (!uid) return
    useSOSStore.getState().startListening(uid)
    usePanicStore.getState().startListening(uid)
    useProfilesStore.getState().fetchProfiles()
    useMessagingStore.getState().subscribeInbox(uid)
    return () => {
      useSOSStore.getState().stopListening()
      usePanicStore.getState().stopListening()
    }
  }, [uid])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        openSearch()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [openSearch])

  return (
    <div className="h-[100dvh] bg-slate-100 overflow-hidden overscroll-none">
      <Sidebar />
      {!hideHeader && <AppHeader scrollHidden={headerScrollHidden} />}
      {/* On desktop: push content right by sidebar width (w-14 mini / w-48 expanded) */}
      <main ref={mainRef} className={`app-shell-main h-[100dvh] overflow-y-auto overscroll-contain bg-slate-100 ${hideHeader ? 'app-shell-main--no-header pt-0' : 'pt-16 lg:pt-0'} pb-24 transition-[padding-left] duration-300 ${sidebarExpanded ? 'lg:pl-52' : 'lg:pl-14'}`}>
        {children}
      </main>
      {!hideFab && <FAB />}
      <AppToasts />
      <GlobalSearchModal />
      <ImpersonationBanner />
    </div>
  )
}
